import React, { useState, useEffect, useMemo } from "react";
import { Table, Container, Row, Col, Alert, Form, Button, Badge } from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import { useStoreState, useStoreActions } from "easy-peasy";
import axios from '../../../services/axios';
import HelperMetiers360 from '../../../services/HelpersMetiers360';
import Loader from "../../../components/widgets/Loader";
import useBackButton from "../../../hooks/useBackButton";
import SelectMultipleItemsWithSearchBar from "../../../components/widgets/selectMultipleItemsWithSearchBar/SelectMultipleItemsWithSearchBar";
import useCustomGetStoreState from "../../../hooks/useCustomGetStoreState";
import SelectSingleClientComponent from "../../../components/widgets/selectClient/SelectSingleClientComponent";
import CustomSelectSingleComponent from "../../../components/widgets/CustomSelectComponent/CustomSelectSingleComponent";
import PipedriveValidationPage from "./PipedriveValidationPage";
import PipedriveSendingPage from "./PipedriveSendingPage";
import AuthorizationChecker from "../../../services/AuthorizationChecker";
import { useModal } from "../../../hooks/useModal";
import useSiretOrganization from "../../../hooks/useSiretOrganization";
import useUaiEstablishment from "../../../hooks/useUaiEstablishment";


const STATUS_VARIANTS = {
    'A traiter': 'primary',
    'Mis en attente': 'warning',
    'Problème de traitement': 'danger',
    'Ignoré': 'secondary',
};

const SUGGESTED_CLIENT_SOURCE_LABELS = {
    axonautId: 'son ID Axonaut',
    uai: 'son UAI',
    siret: 'son SIRET',
};

const UAI_INVALID_MESSAGE = "UAI invalide : 7 chiffres suivis d'une lettre de contrôle correcte attendus.";

// Surrounding whitespace is stripped on submit, so it shouldn't make an email invalid
const isEmailInvalid = (email) => !!email?.trim() && !HelperMetiers360.isValidEmail(email.trim());

const withTrimmedEmail = (contact) => contact && { ...contact, email: contact.email?.trim() ?? contact.email };

const formatSuggestedClientSources = (sources) =>
    (sources ?? []).map(source => SUGGESTED_CLIENT_SOURCE_LABELS[source] ?? source).join(' et ');

const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    const date = typeof timestamp === 'number'
        ? new Date(timestamp * 1000)
        : new Date(timestamp);
    return date.toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const EditButton = ({ onClick, disabled }) => (
    <div className="text-center">
        <Button variant="secondary" size="sm" className="p-2 m-1" onClick={onClick} disabled={disabled}> <i className="fas fa-edit" /></Button>
    </div>
);

const formatAddress = (num, route, cp, locality, country) => {
    const street = [num, route].filter(Boolean).join(' ') || null;
    return [street, cp, locality, country].filter(Boolean).join(', ') || '-';
};

const resolveNames = (ids, list, idKey = 'id', nameKey = 'name') =>
    (ids ?? []).map(id => list?.find(item => String(item[idKey]) === String(id))?.[nameKey] ?? String(id)).join(', ') || '-';

const resolveStatus = (val, list) =>
    list?.find(s => String(s.id) === String(val))?.name ?? (val ? String(val) : '-');

const ContactFields = ({ contact }) => <>
    <div><span className="fw-bold text-muted me-1">Nom :</span>{contact.name || '-'}</div>
    <div><span className="fw-bold text-muted me-1">Fonction :</span>{contact.jobTitle || '-'}</div>
    <div><span className="fw-bold text-muted me-1">Email :</span>{contact.email || '-'}</div>
    <div><span className="fw-bold text-muted me-1">Téléphone :</span>{contact.phone || '-'}</div>
</>;


const PipedriveDetailPage = () => {
    const { uniqueId } = useParams();
    const { apiData } = useStoreState(state => state.actionSlugs);
    const [message, setMessage] = useState(null);
    const [isFetching, setIsFetching] = useState(true);
    const [formData, setFormData] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [note, setNote] = useState('');
    const [step, setStep] = useState(1);
    const [parentEditing, setParentEditing] = useState(false);
    const [selectedSources, setSelectedSources] = useState({});
    const [editingFields, setEditingFields] = useState({});
    const [editSnapshots, setEditSnapshots] = useState({});
    const [contactEmailToFocus, setContactEmailToFocus] = useState(null);
    const [selectedClientUUIDs, setSelectedClientUUIDs] = useState(null);
    const [axonautId, setAxonautId] = useState(null);
    const [axonautInput, setAxonautInput] = useState('');
    const { backButtonComponent } = useBackButton();
    const pipedriveSlugs = useStoreState(state => state.actionSlugs.actionSlugsDispatcher('pipedriveMessage'));
    const navigate = useNavigate();
    const { modalComponent, setModalShow, modalData, setModalData, setIsSending } = useModal();

    const { fetchClientsCategories, fetchContactsM360, fetchClientsCRM } = useStoreActions(actions => actions.clients);
    const { allClientStatus, isFetchingAllClientStatus } = useStoreState(state => state.clientStatus);
    const { fetchClientStatus } = useStoreActions(actions => actions.clientStatus);
    const { fetchTagOperations } = useStoreActions(actions => actions.tagOperations);
    const { contactsM360, clientsCategories } = useStoreState(state => state.clients);
    const { allTagOperations } = useStoreState(state => state.tagOperations);
    const { allRelationTypes } = useStoreState(state => state.relationTypes);
    const { fetchRelationTypes } = useStoreActions(actions => actions.relationTypes);
    const { allClients, isFetchingAllClients, allClientsCRM, isFetchingAllClientsCRM } = useCustomGetStoreState('clients');


    const pipedriveClient = message?.pipedriveClient;
    const contacts = message?.pipedriveContacts ?? [];
    const boClient = message?.clientBoData?.client;

    const messageWasSentToAtLeastOneService = message?.isAxonautSent || message?.isBOSent || message?.isPipedriveSent;

    const pipedriveData = useMemo(() => {
        if (!message) return null;
        const pip = message?.pipedriveClient ?? {};
        const pipParent = allClients?.find(c => c.uniqueId === pip.parentalite);
        const categoryIds = (pip.categoriesPipedrive ?? [])
            .map(catId => clientsCategories?.find(cat => cat.idPipedrive == catId)?.id)
            .filter(Boolean);
        const tagOperationIds = (pip.tagOperations ?? [])
            .map(name => allTagOperations?.find(t => t.name === name)?.id)
            .filter(Boolean);
        const natureRelationIds = (pip.natureRelation ?? [])
            .map(id => allRelationTypes?.find(n => n.idPipedrive == id)?.id)
            .filter(Boolean);
        const parentClientLabel = pipParent?.clientName ?? (pip.parentPipedriveId ? `Parent non enregistré, ${pip.parentPipedriveId}` : '-');
        return {
            clientName: message?.clientName ?? '-',
            siret: pip.siret ?? '-',
            uai: pip.uai ?? '-',
            idAxonaut: pip.idAxonaut ?? '-',
            backofficeUUID: pip.idBackoffice ?? '-',
            address: formatAddress(pip.addressStreetNumber, pip.addressRoute, pip.addressPostalCode, pip.addressLocality, pip.addressCountry),
            addressStreet: [pip.addressStreetNumber, pip.addressRoute].filter(Boolean).join(' ') || '-',
            addressLocality: pip.addressLocality ?? '',
            addressPostalCode: pip.addressPostalCode ?? '',
            addressCountry: pip.addressCountry ?? '',
            addressStreetNumber: pip.addressStreetNumber ?? '',
            addressRoute: pip.addressRoute ?? '',
            parentClient: pip.parentRelationType === 'related' && parentClientLabel !== '-'
                ? <><i className="fas fa-exclamation-triangle text-warning">&nbsp;</i>{parentClientLabel} (relation "liée", pas "parent")</>
                : parentClientLabel,
            parentClientText: parentClientLabel,
            parentClientId: pipParent?.uniqueId ?? null,
            parentRelationType: pip.parentRelationType ?? null,
            parentNeedsBOAccess: !!pip.parentNeedsBOAccess,
            categories: resolveNames(categoryIds, clientsCategories),
            tagOperations: resolveNames(tagOperationIds, allTagOperations),
            status: resolveStatus(pip.status, allClientStatus),
            natureRelation: resolveNames(natureRelationIds, allRelationTypes),
            contactsM360: null,
            contacts: (message?.pipedriveContacts ?? []).map(c => ({
                id: c.id,
                name: c.name ?? '',
                jobTitle: c.jobTitle ?? '',
                email: c.email ?? '',
                phone: c.phone ?? '',
            })),
        };
    }, [message, allClients, clientsCategories, allTagOperations, allRelationTypes, allClientStatus]);

    const boData = useMemo(() => {
        if (!boClient) return null;
        const boParent = allClients?.find(c => c.uniqueId === boClient.parentalite);
        return {
            clientName: boClient.name ?? '-',
            siret: boClient.siret ?? '-',
            uai: boClient.uai ?? '-',
            idAxonaut: boClient.idAxonaut ?? '-',
            backofficeUUID: boClient.idBackoffice ?? '-',
            address: formatAddress(boClient.addressStreetNumber, boClient.addressRoute, boClient.addressPostalCode, boClient.addressLocality, boClient.addressCountry),
            addressStreet: [boClient.addressStreetNumber, boClient.addressRoute].filter(Boolean).join(' ') || '-',
            addressLocality: boClient.addressLocality ?? '',
            addressPostalCode: boClient.addressPostalCode ?? '',
            addressCountry: boClient.addressCountry ?? '',
            addressStreetNumber: boClient.addressStreetNumber ?? '',
            addressRoute: boClient.addressRoute ?? '',
            parentClient: boParent?.clientName ?? (boClient.parentalite ? 'Parent non enregistré' : '-'),
            parentClientId: boParent?.uniqueId ?? null,
            hasParent: !!boClient.parentalite,
            categories: resolveNames(boClient.categories, clientsCategories),
            tagOperations: resolveNames(boClient.tagOperations, allTagOperations),
            status: resolveStatus(boClient.statut, allClientStatus),
            natureRelation: resolveNames(boClient.natureRelation, allRelationTypes),
            contactsM360: resolveNames(boClient.contactClientM360, contactsM360),
            contacts: (message.clientBoData?.contacts ?? []).map(c => ({
                id: c.uniqueId,
                idPipedrive: c.idPipedrive,
                name: c.name ?? '',
                jobTitle: c.jobTitle ?? '',
                email: c.email ?? '',
                phone: c.phone ?? '',
            })),
        };
    }, [message, allClients, clientsCategories, allTagOperations, allRelationTypes, allClientStatus, contactsM360]);

    const derivedCategories = useMemo(() =>
        boClient?.categories?.length > 0
            ? boClient.categories
            : (pipedriveClient?.categoriesPipedrive ?? []).map(id => clientsCategories?.find(c => c.idPipedrive == id)?.id).filter(Boolean),
        [boClient, pipedriveClient, clientsCategories]);
    const derivedTagOperations = useMemo(() =>
        boClient?.tagOperations?.length > 0
            ? boClient.tagOperations
            : (pipedriveClient?.tagOperations ?? []).map(name => allTagOperations?.find(t => t.name === name)?.id).filter(Boolean),
        [boClient, pipedriveClient, allTagOperations]);
    const derivedNatureRelation = useMemo(() =>
        boClient?.natureRelation?.length > 0
            ? boClient.natureRelation
            : (pipedriveClient?.natureRelation ?? []).map(id => allRelationTypes?.find(n => n.idPipedrive == id)?.id).filter(Boolean),
        [boClient, pipedriveClient, allRelationTypes]);

    const setField = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

    // Server is the source of truth for the manually-attached BO client — deriving it
    // from the response (instead of trusting the value optimistically) means a failed
    // update simply leaves the previous selection in place.
    const resolveSelectedClientUUIDs = (data) =>
        !data.pipedriveClient?.idBackoffice && data.newData?.backofficeUUID
            ? [data.newData.backofficeUUID]
            : null;

    useEffect(() => {
        if (!message) {
            setIsFetching(true);

            const getOneUrl = apiData.ACTIONS?.pipedriveMessage?.readOne?.url?.replace('uniqueId', uniqueId);

            axios.get(getOneUrl)
                .then(data => {
                    setMessage(data);
                    setNote(data.note ?? '');
                    setSelectedClientUUIDs(resolveSelectedClientUUIDs(data));
                })
                // The interceptor already toasts the error; this catch only prevents
                // an unhandled rejection from being reported as a JS error (e.g. to Datadog RUM)
                .catch(() => { })
                .finally(() => setIsFetching(false));
        }
    }, [uniqueId]);

    useEffect(() => {
        if (message) {
            const nd = message?.newData ?? {};
            const pip = message?.pipedriveClient;
            const bo = message?.clientBoData?.client;

                // newData first, then pip if same as bo or bo is empty, then bo if pip is empty, else default to pip when both are present but conflicting
                const pick = (ndVal, pipVal, boVal) => {
                    if (ndVal !== undefined) return ndVal;
                    if (!bo) return pipVal ?? null;
                    const p = pipVal ?? '', b = boVal ?? '';
                    if (!b || p === b) return p || null;
                    if (!p) return b || null;
                    return p || null;
                };

                // Resolve which address source to use (treated as a unit)
            const addrKeys = ['addressLocality', 'addressPostalCode', 'addressRoute', 'addressStreetNumber', 'addressCountry'];
                const addrSrc = (() => {
                    if (nd.addressLocality !== undefined) return null;
                    if (!bo) return pip;
                    if (!bo.addressLocality) return pip;
                    if (!pip?.addressLocality) return bo;
                    const countFilled = (src) => addrKeys.filter(k => src?.[k]).length;
                    return countFilled(pip) >= countFilled(bo) ? pip : bo;
                })();
                const addrField = (k) => nd[k] !== undefined ? nd[k] : addrSrc ? (addrSrc[k] ?? '') : null;

                // Resolve parentClientUuid
                const parentClientUuid = (() => {
                    if (nd.parentClientUuid !== undefined) return nd.parentClientUuid;
                    const pipP = pip?.parentalite ?? null;
                    if (!bo) return pipP ? [String(pipP)] : [];
                    const boP = bo?.parentalite ?? null;
                    if (pipP === boP || !boP) return pipP ? [String(pipP)] : [];
                    if (!pipP) return boP ? [String(boP)] : [];
                    return null;
                })();

            const boContactsData = message.clientBoData?.contacts ?? [];
            const autoAddedContacts = nd.contacts !== undefined
                ? []
                : (message.pipedriveContacts ?? [])
                    .filter(c => !boContactsData.find(bc => c.id == bc.idPipedrive || (c.email && bc.email === c.email)))
                    .map(c => ({ id: c.id, name: c.name ?? '', jobTitle: c.jobTitle ?? '', email: c.email ?? '', phone: c.phone ?? '' }));
            const ndContacts = nd.contacts ?? [];

            setFormData({
                clientName: pick(nd.clientName, message.clientName, bo?.name),
                siret: pick(nd.siret, pip?.siret, bo?.siret),
                uai: pick(nd.uai, pip?.uai, bo?.uai),
                addressStreetNumber: addrField('addressStreetNumber'),
                addressRoute: addrField('addressRoute'),
                addressPostalCode: addrField('addressPostalCode'),
                addressLocality: addrField('addressLocality'),
                addressCountry: addrField('addressCountry'),
                contacts: [...ndContacts, ...autoAddedContacts],
                    categories: nd.categories !== undefined ? nd.categories : null,
                    tagOperations: nd.tagOperations !== undefined ? nd.tagOperations : null,
                clientStatus: nd.clientStatus !== undefined ? nd.clientStatus : null,
                    natureRelation: nd.natureRelation !== undefined ? nd.natureRelation : null,
                    contactsM360: nd.contactsM360 !== undefined ? nd.contactsM360 : null,
                    parentClientUuid,
                    billingContactId: nd.billingContactId ?? null,
                    clientInvoiced: nd.clientInvoiced ?? 'self',
            });

            const autoSources = {};
            if (addrSrc && bo) autoSources.address = addrSrc === pip ? 'pipedrive' : 'bo';
            autoAddedContacts.forEach(c => { autoSources[`contact_${c.id}`] = 'pipedrive'; });
            if (Object.keys(autoSources).length > 0) {
                setSelectedSources(prev => ({ ...prev, ...autoSources }));
            }
        }
    }, [message]);

    useEffect(() => { allClientStatus?.length === 0 && fetchClientStatus(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => { allTagOperations?.length === 0 && fetchTagOperations(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => { clientsCategories?.length === 0 && fetchClientsCategories(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => { allRelationTypes?.length === 0 && fetchRelationTypes(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => { contactsM360?.length === 0 && fetchContactsM360(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => { allClientsCRM?.length === 0 && fetchClientsCRM(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (step !== 1) window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [step]);

    // Bring the email field into view once the contact row has switched to edit mode
    useEffect(() => {
        if (contactEmailToFocus == null) return;
        const emailInput = document.getElementById(`contact-email-${contactEmailToFocus}`);
        emailInput?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        emailInput?.focus({ preventScroll: true });
        setContactEmailToFocus(null);
    }, [contactEmailToFocus]);

    const validatedSiret = formData?.siret && !editingFields.siret && HelperMetiers360.isValidSiret(formData.siret)
        ? formData.siret
        : null;
    const siretLookup = useSiretOrganization(validatedSiret);

    const validatedUai = formData?.uai && !editingFields.uai && HelperMetiers360.isValidUai(formData.uai)
        ? formData.uai
        : null;
    const uaiLookup = useUaiEstablishment(validatedUai);

    const setContactField = (id, field, value) => setFormData(prev => ({
        ...prev,
        contacts: prev.contacts.map(c => c.id === id ? { ...c, [field]: value } : c),
    }));

    const addressSnapshot = () => ({
        addressStreetNumber: formData.addressStreetNumber,
        addressRoute: formData.addressRoute,
        addressPostalCode: formData.addressPostalCode,
        addressLocality: formData.addressLocality,
        addressCountry: formData.addressCountry,
    });

    const initAddress = (sourceData) => setFormData(prev => ({
        ...prev,
        addressStreetNumber: sourceData.addressStreetNumber ?? '',
        addressRoute: sourceData.addressRoute ?? '',
        addressPostalCode: sourceData.addressPostalCode ?? '',
        addressLocality: sourceData.addressLocality ?? '',
        addressCountry: sourceData.addressCountry ?? '',
    }));

    const cancelAddress = () => setFormData(prev => ({
        ...prev,
        addressStreetNumber: null,
        addressRoute: null,
        addressPostalCode: null,
        addressLocality: null,
        addressCountry: null,
    }));

    const restoreAddress = () => setFormData(prev => ({ ...prev, ...editSnapshots.address }));

    const initContactFrom = (id, source, boUniqueId = null) => setFormData(prev => {
        const entry = { id, ...(boUniqueId != null && { boUniqueId }), name: source.name ?? '', jobTitle: source.jobTitle ?? '', email: source.email ?? '', phone: source.phone ?? '' };
        const exists = prev.contacts.some(c => c.id === id);
        return { ...prev, contacts: exists ? prev.contacts.map(c => c.id === id ? entry : c) : [...prev.contacts, entry] };
    });

    const cancelContact = (id) => setFormData(prev => ({
        ...prev,
        contacts: prev.contacts.filter(c => c.id !== id),
        ...(String(prev.billingContactId) === String(id) ? { billingContactId: null } : {}),
    }));

    const restoreContact = (id) => setFormData(prev => ({
        ...prev,
        contacts: prev.contacts.map(c => c.id === id ? editSnapshots[`contact_${id}`] : c),
    }));


    const findBillingContact = () => (formData.contacts ?? []).find(c => String(c.id) === String(formData.billingContactId))
        ?? contacts.find(c => String(c.id) === String(formData.billingContactId));

    const submitEdit = (status, isValidate = false) => {
        const editUrl = apiData.ACTIONS?.pipedriveMessage?.update?.url?.replace('uniqueId', uniqueId);
        const csrfToken = apiData.ACTIONS?.pipedriveMessage?.update?.csrfToken;

        const billingContact = findBillingContact();
        // Replace null sentinel fields with their displayed defaults so they're captured on submit
        const effectiveFormData = {
            ...formData,
            // Normalize identifiers (keep null / empty values untouched)
            siret: formData.siret?.replace(/\s/g, '') || formData.siret,
            uai: formData.uai?.replace(/\s/g, '').toUpperCase() || formData.uai,
            categories: formData.categories ?? derivedCategories ?? [],
            tagOperations: formData.tagOperations ?? derivedTagOperations ?? [],
            natureRelation: formData.natureRelation ?? derivedNatureRelation ?? [],
            clientStatus: formData.clientStatus ?? boClient?.statut ?? null,
            contactsM360: formData.contactsM360 ?? boClient?.contactClientM360 ?? [],
            idAxonaut: axonautId ?? pipedriveClient?.idAxonaut ?? boClient?.idAxonaut ?? null,
            contacts: (formData.contacts ?? []).map(withTrimmedEmail),
            billingContact: withTrimmedEmail(billingContact),
            billingContactId: billingContact ? formData.billingContactId : null,
            // Billing the parent no longer makes sense once the parent was removed
            clientInvoiced: formData.parentClientUuid?.length ? (formData.clientInvoiced ?? 'self') : 'self',
            // selectedClientUUIDs state comes from CustomSelectSingleComponent as an array — the backend expects a scalar
            backofficeUUID: pipedriveClient?.idBackoffice
                ?? (Array.isArray(selectedClientUUIDs) ? (selectedClientUUIDs[0] ?? null) : selectedClientUUIDs)
                ?? null,
        };
        setIsSubmitting(true);
        axios.post(editUrl, { status, newData: effectiveFormData, note, csrfToken })
            .then((data) => {
                if (isValidate) {
                    setStep(2);
                    setMessage(data);
                } else {
                    navigate(pipedriveSlugs.readAll);
                }
            })
            .catch(() => { })
            .finally(() => setIsSubmitting(false));
    };


    const updateSelectedClient = (uuids) => {
        const editUrl = apiData.ACTIONS?.pipedriveMessage?.update?.url?.replace('uniqueId', uniqueId);
        const csrfToken = apiData.ACTIONS?.pipedriveMessage?.update?.csrfToken;
        const backofficeUUID = Array.isArray(uuids) ? (uuids[0] ?? null) : uuids;
        setIsSubmitting(true);
        axios.post(editUrl, { newData: { backofficeUUID }, note, csrfToken })
            .then((data) => {
                setMessage(data);
                setSelectedClientUUIDs(resolveSelectedClientUUIDs(data));
            })
            .catch(() => { })
            .finally(() => setIsSubmitting(false));
    };

    // Resolves to true on success, false on failure (never rejects, so callers
    // that ignore the result — e.g. sendStatus(6) in the sending page — are safe)
    const sendStatus = (status) => {
        const editUrl = apiData.ACTIONS?.pipedriveMessage?.update?.url?.replace('uniqueId', uniqueId);
        const csrfToken = apiData.ACTIONS?.pipedriveMessage?.update?.csrfToken;
        setIsSubmitting(true);
        return axios.post(editUrl, { status, csrfToken })
            .then((data) => { setMessage(data); return true; })
            .catch(() => false)
            .finally(() => setIsSubmitting(false));
    };

    const deleteMessage = () => {
        const deleteUrl = apiData.ACTIONS?.pipedriveMessage?.delete?.url?.replace('uniqueId', uniqueId);
        const csrfToken = apiData.ACTIONS?.pipedriveMessage?.delete?.csrfToken;
        return axios.post(deleteUrl, { csrfToken })
            .then(() => navigate(pipedriveSlugs.readAll));
    };

    const createDeleteModal = () => {
        setModalData({
            ...modalData,
            header: <>Suppression du message Pipedrive</>,
            content: <h5>Êtes-vous sûr⋅e de vouloir supprimer ce message Pipedrive&nbsp;? Cette action est irréversible.</h5>,
            resetButton: 'Supprimer',
            onReset: () => {
                setIsSending(true);
                deleteMessage().catch(() => setIsSending(false));
            },
        });
        setModalShow(true);
    };

    const setParent = (uuid) => setFormData(prev => ({
        ...prev,
        parentClientUuid: uuid,
        // Removing the parent while "Client Facturé" points at it would send an
        // inconsistent payload (406 backend) with no way back from the sending page
        ...(!uuid?.length && prev.clientInvoiced === 'parent' ? { clientInvoiced: 'self' } : {}),
    }));

    const startEditing = (field, snapshot) => {
        setEditingFields(prev => ({ ...prev, [field]: true }));
        setEditSnapshots(prev => ({ ...prev, [field]: snapshot }));
    };
    const stopEditing = (field) => setEditingFields(prev => { const next = { ...prev }; delete next[field]; return next; });
    const cancelEditing = (field, restoreFn) => {
        (restoreFn ?? (() => setField(field, editSnapshots[field])))();
        stopEditing(field);
    };
    const selectSource = (field, source) => {
        setSelectedSources(prev => ({ ...prev, [field]: source }));
        stopEditing(field);
    };
    const deselect = (field, resetFn) => {
        resetFn();
        setSelectedSources(prev => { const next = { ...prev }; delete next[field]; return next; });
        stopEditing(field);
    };

    if (isFetching) return <Loader />;

    const lookups = { allClients, clientsCategories, allTagOperations, allRelationTypes, allClientStatus, contactsM360List: contactsM360 };

    // Anomaly detected by the backend (id mismatch…): show it before any routing,
    // including to the sending page — the operator must arbitrate first
    if (message?.error) {
        return <Container fluid>
            <Row className="mb-3 align-items-center">
                <Col><h2 className="mb-0">{message.title ?? 'Message Pipedrive'}</h2></Col>
                <Col><Badge className="m-2 p-2" bg={STATUS_VARIANTS[message.status] ?? 'secondary'}>{message.status}</Badge></Col>
                <Col className="col-auto">{backButtonComponent}</Col>
            </Row>
            <Row>
                <Col>
                    <strong>Date:</strong> {formatDate(message.date)}
                </Col>
            </Row>
            <Row className="m-4">
                <Alert variant="danger">Veuillez résoudre cette erreur avant de procéder au traitement de cette action: <br />
                    {message?.error}</Alert>
            </Row>
        </Container>;
    }

    if ((step === 3 && pipedriveData && formData) || messageWasSentToAtLeastOneService || message?.statusValue == 3 || message?.statusValue == 5) return (
        <PipedriveSendingPage
            message={message}
            setMessage={setMessage}
            sendStatus={sendStatus}
            autoSend={step === 3}
            onBackToQualification={() => {
                // Status must change first: as long as statusValue is 5 (or 3) the
                // routing clause above would remount the sending page despite step 1
                sendStatus(2).then((ok) => { if (ok) setStep(1); });
            }}
        />
    );

    if (step === 2 && pipedriveData && formData) return (
        <PipedriveValidationPage
            pipedriveData={pipedriveData}
            boData={boData}
            message={message}
            note={note}
            lookups={lookups}
            isSubmitting={isSubmitting}
            onBack={() => setStep(1)}
            onConfirm={() => {
                // Wait for the status update before mounting the sending page,
                // otherwise its stale message snapshot can re-trigger a send.
                // On failure, stay on the validation page (the axios interceptor
                // already toasts the error) so the operator can retry.
                sendStatus(5).then((ok) => { if (ok) setStep(3); });
            }}
        />
    );

    if (!message) return (
        <Container fluid>
            <Alert variant="danger" className="mt-3">Message introuvable.</Alert>
        </Container>
    );

    if (!formData) return <Loader />;

    const isSiretInvalid = !!formData.siret && !HelperMetiers360.isValidSiret(formData.siret);
    const isUaiInvalid = !!formData.uai && !HelperMetiers360.isValidUai(formData.uai);

    const isBillingContactSelectable = message.type === "deal" && (formData.clientInvoiced ?? 'self') === 'self';
    const billingContact = isBillingContactSelectable ? findBillingContact() : null;
    const isBillingEmailInvalid = isEmailInvalid(billingContact?.email);
    const isBillingContactKept = !!billingContact && (formData.contacts ?? []).some(c => String(c.id) === String(billingContact.id));
    const invalidEmailContacts = (formData.contacts ?? []).filter(c => isEmailInvalid(c.email));

    // The billing contact can only be edited once it is part of the kept contacts:
    // keep it, open its row in edit mode and bring its email field into view
    const fixBillingContactEmail = () => {
        const contactKey = `contact_${billingContact.id}`;
        const boContact = message.clientBoData?.contacts?.find(c => billingContact.id == c.idPipedrive || c.email === billingContact.email);
        initContactFrom(billingContact.id, billingContact, boContact?.uniqueId ?? null);
        selectSource(contactKey, 'pipedrive');
        startEditing(contactKey, { id: billingContact.id, ...(boContact && { boUniqueId: boContact.uniqueId }), name: billingContact.name ?? '', jobTitle: billingContact.jobTitle ?? '', email: billingContact.email ?? '', phone: billingContact.phone ?? '' });
        setContactEmailToFocus(billingContact.id);
    };

    const siretOrganizationInfo = () => {
        if (!validatedSiret) return null;
        const { isLoading, organization, error } = siretLookup;
        if (isLoading) return <div className="text-muted small mt-1"><i className="fas fa-spinner fa-spin" /> Recherche de l'organisation...</div>;
        if (error) return <div className="text-warning small mt-1">{error}</div>;
        if (!organization) return null;
        return <div className="small mt-1">
            <strong>{organization.name}</strong>
            {organization.establishmentName && <> — {organization.establishmentName}</>}
            {organization.city && <span className="text-muted"> ({organization.city})</span>}
            {organization.isClosed && <Badge bg="danger" className="ms-1">Établissement fermé</Badge>}
        </div>;
    };

    const uaiEstablishmentInfo = () => {
        if (!validatedUai) return null;
        const { isLoading, establishment, error } = uaiLookup;
        if (isLoading) return <div className="text-muted small mt-1"><i className="fas fa-spinner fa-spin" /> Recherche de l'établissement...</div>;
        if (error) return <div className="text-warning small mt-1">{error}</div>;
        if (!establishment) return null;
        return <div className="small mt-1">
            <strong>{establishment.name}</strong>
            {establishment.type && <> — {establishment.type}{establishment.status && ` ${establishment.status.toLowerCase()}`}</>}
            {establishment.city && <span className="text-muted"> ({establishment.city})</span>}
            {establishment.isClosing && <Badge bg="warning" text="dark" className="ms-1">Fermeture prévue</Badge>}
        </div>;
    };

    const axonautSection = () => {
        if (pipedriveClient?.idAxonaut && boClient?.idAxonaut && pipedriveClient.idAxonaut != boClient.idAxonaut) {
            return <Alert variant="danger">
                Ce client dispose d'un id Axonaut indiqué dans le BO différent de celui indiqué dans Pipedrive. Merci de régulariser cela avant de traiter cette action.
            </Alert>;
        }
        const resolvedAxonautId = pipedriveClient?.idAxonaut ?? boClient?.idAxonaut ?? null;
        if (resolvedAxonautId) {
            return <Col className="col-auto">
                <a href={"https://axonaut.com/business/company/show/" + resolvedAxonautId}
                    target='_blank'
                    rel="noopener noreferrer"
                    className="target-axonaut">
                    <Button size="sm" className="left-button d-flex align-items-center axonaut-btn">
                        <div className="bubbleLogoAxonaut">
                            <span></span>
                        </div>
                        Lien Axonaut
                    </Button>
                </a>
            </Col>;
        }
        if (axonautId) {
            return <div className="d-flex">
                <a href={"https://axonaut.com/business/company/show/" + axonautId}
                    target='_blank'
                    rel="noopener noreferrer"
                    className="target-axonaut">
                    <Button size="sm" className="left-button d-flex align-items-center axonaut-btn">
                        <div className="bubbleLogoAxonaut">
                            <span></span>
                        </div>
                        Lien Axonaut
                    </Button>
                </a>
                    <Button variant="danger" size="sm" className="p-2 m-1" onClick={() => { setAxonautId(null); setAxonautInput(''); }}>
                        <i className="fas fa-redo" />
                </Button>
            </div>;
        }
        return <>
            <Col className="col-auto">
                <strong>Je veux le relier au client Axonaut ayant l'id :</strong>
            </Col>
            <Col>
                <div className="d-flex gap-1 align-items-center">
                    <Form.Control
                        size="sm"
                        style={{ width: '10rem' }}
                        value={axonautInput}
                        onChange={e => setAxonautInput(e.target.value)}
                        placeholder="ID Axonaut"
                    />
                    <Button
                        variant="success"
                        size="sm"
                        className="p-2 m-1"
                        disabled={!axonautInput}
                        onClick={() => setAxonautId(axonautInput)}
                    >
                        <i className="fas fa-check" />
                    </Button>
                </div>
            </Col>
        </>;
    };


    const invoiceOptions = [
        <option key='self'
            value={'self'}>
            {`Le client lui-même : ${formData?.clientName ?? message?.clientName}`}
        </option>,
        <option key='parent'
            value={"parent"}
            disabled={!formData?.parentClientUuid?.length}>
            {formData?.parentClientUuid?.length > 0 ? `Son parent : ${allClients?.find(c => c.uniqueId === formData.parentClientUuid[0])?.clientName ?? formData.parentClientUuid[0]}` : "Ce client n'a pas de parent indiqué dans le BO"}
        </option>,
    ];

    const handleInvoiceSelect = (event) => {
        setField('clientInvoiced', [...event.target.selectedOptions].map(opt => opt.value)[0]);
    }

    return (
        <Container fluid>
            <Row className="mb-3 align-items-center">
                <Col><h2 className="mb-0">{message.title ?? 'Message Pipedrive'}</h2></Col>
                <Col><Badge className="m-2 p-2" bg={STATUS_VARIANTS[message.status] ?? 'secondary'}>{message.status}</Badge></Col>
                <Col className="col-auto">{backButtonComponent}</Col>
            </Row>
            <Row>
                <Col>
                    <strong>Date:</strong> {formatDate(message.date)}
                </Col>
            </Row>
            <Row className="m-4 d-flex align-items-center">
                <Col className="col-auto ">
                    <a href={"https://metiers360.pipedrive.com/organization/" + pipedriveClient?.pipedriveId}
                        target='_blank'
                        rel="noopener noreferrer"
                        className="target-pipedrive">
                        <Button size="sm" className="left-button d-flex align-items-center pipedrive-btn">
                            <div className="bubbleLogoPipedrive">
                                <span></span>
                            </div>
                            Lien Pipedrive
                        </Button>
                    </a>
                </Col>
                <Col className="col-auto ">
                    {axonautSection()}
                </Col>
            </Row>
            {!pipedriveClient?.idBackoffice &&
                <Row className="m-4 d-flex justify-content-between align-items-center">
                    {isFetchingAllClientsCRM ?
                        <Loader />
                        :
                        (selectedClientUUIDs ?
                            <>
                                <Col className="col-auto ">
                                    <strong>Relié au client : {allClientsCRM?.find(c => c.uniqueId === selectedClientUUIDs?.[0])?.name}</strong>
                                </Col>
                                <Col>
                                    <Button variant="danger" size="sm" className="p-2 m-1" disabled={isSubmitting} onClick={() => updateSelectedClient(null)}><i className="fas fa-redo" /></Button>
                                </Col>
                            </>
                            : <>
                                {message?.suggestedClient &&
                                    <Col xs={12} className="mb-2">
                                        <Alert variant="warning" className="d-flex justify-content-between align-items-center mb-0">
                                            <span>Client probable trouvé côté CRM via {formatSuggestedClientSources(message.suggestedClient.sources)} : <strong>{message.suggestedClient.clientName}</strong> — à vérifier avant de rattacher.</span>
                                            <Button variant="warning" size="sm" disabled={isSubmitting} onClick={() => updateSelectedClient([message.suggestedClient.clientUniqueId])}>Rattacher</Button>
                                        </Alert>
                                    </Col>
                                }
                                <Col className="col-auto ">
                                    <strong>Relier à un client CRM existant :</strong>
                                </Col>
                                <Col>
                                    <CustomSelectSingleComponent
                                        selectId="clientUUIDSelector"
                                        nameSelect="clientUUIDSelector"
                                        options={allClientsCRM?.filter(c => !c.idPipedrive) ?? []}
                                        selectedOptions={[]}
                                        setSelectedOptions={updateSelectedClient}
                                        optionLabel="name"
                                        mustRedirect={false}
                                    />
                                </Col>
                            </>
                        )}
                </Row>}
            <Row>
                <h3 className="mb-4 mt-4">Informations Client</h3>
                <Table striped bordered>
                    <thead>
                        <tr>
                            <th>#</th>
                            <th>Pipedrive</th>
                            {boClient && <th>Backoffice</th>}
                            <th>Informations gardées</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>Nom client</td>
                            <td
                                {...(boClient && { onClick: () => { setField('clientName', message.clientName ?? ''); selectSource('clientName', 'pipedrive'); }, style: { cursor: 'pointer', ...(selectedSources.clientName === 'pipedrive' && { fontWeight: 'bold' }) }, title: 'Cliquer pour sélectionner' })}
                            >
                                {pipedriveData.clientName}
                            </td>
                            {boClient && <td
                                onClick={() => { setField('clientName', boClient.name ?? ''); selectSource('clientName', 'bo'); }}
                                style={{ cursor: 'pointer', ...(selectedSources.clientName === 'bo' && { fontWeight: 'bold' }) }}
                                title="Cliquer pour sélectionner"
                            >
                                {boData.clientName}
                            </td>}
                            <td className="text-center">
                                {formData.clientName === null
                                    ? (boClient && (message.clientName || boClient.name))
                                        ? <span className="text-muted fst-italic small">Sélectionnez une source ci-contre</span>
                                        : <EditButton onClick={() => { startEditing('clientName', null); setField('clientName', message.clientName ?? boClient?.name ?? ''); }} />
                                    : editingFields.clientName
                                        ? <div className="d-flex gap-1 align-items-center justify-content-center">
                                            <Form.Control size="sm" value={formData.clientName} onChange={e => setField('clientName', e.target.value)} />
                                            <Button variant="success" size="sm" className="p-2 m-1" onClick={() => stopEditing('clientName')}><i className="fas fa-check" /></Button>
                                            <Button variant="outline-secondary" size="sm" className="p-2 m-1" onClick={() => cancelEditing('clientName')}><i className="fas fa-times" /></Button>
                                        </div>
                                        : <div className="d-flex gap-1 align-items-center justify-content-center">
                                            <span>{formData.clientName || <em className="text-muted">(vide)</em>}</span>
                                            <EditButton onClick={() => startEditing('clientName', formData.clientName)} />
                                            <Button variant="danger" size="sm" className="p-2 m-1" onClick={() => deselect('clientName', () => setField('clientName', null))}><i className="fas fa-redo" /></Button>
                                        </div>
                                }
                            </td>
                        </tr>
                        <tr>
                            <td>SIRET</td>
                            <td
                                {...(boClient && { onClick: () => { setField('siret', pipedriveClient?.siret ?? ''); selectSource('siret', 'pipedrive'); }, style: { cursor: 'pointer', ...(selectedSources.siret === 'pipedrive' && { fontWeight: 'bold' }) }, title: 'Cliquer pour sélectionner' })}
                            >
                                {pipedriveData.siret}
                            </td>
                            {boClient && <td
                                onClick={() => { setField('siret', boClient.siret ?? ''); selectSource('siret', 'bo'); }}
                                style={{ cursor: 'pointer', ...(selectedSources.siret === 'bo' && { fontWeight: 'bold' }) }}
                                title="Cliquer pour sélectionner"
                            >
                                {boData.siret}
                            </td>}
                            <td className="text-center">
                                {formData.siret === null
                                    ? (boClient && (pipedriveClient?.siret || boClient.siret))
                                        ? <span className="text-muted fst-italic small">Sélectionnez une source ci-contre</span>
                                        : <EditButton onClick={() => { startEditing('siret', null); setField('siret', pipedriveClient?.siret ?? boClient?.siret ?? ''); }} />
                                    : editingFields.siret
                                        ? <div>
                                            <div className="d-flex gap-1 align-items-center justify-content-center">
                                                <Form.Control size="sm" value={formData.siret} isInvalid={isSiretInvalid} onChange={e => setField('siret', e.target.value)} />
                                                <Button variant="success" size="sm" className="p-2 m-1" disabled={isSiretInvalid} onClick={() => stopEditing('siret')}><i className="fas fa-check" /></Button>
                                                <Button variant="outline-secondary" size="sm" className="p-2 m-1" onClick={() => cancelEditing('siret')}><i className="fas fa-times" /></Button>
                                            </div>
                                            {isSiretInvalid &&
                                                <div className="text-danger small mt-1">SIRET invalide : 14 chiffres attendus avec une clé de contrôle correcte.</div>
                                            }
                                        </div>
                                        : <div>
                                            <div className="d-flex gap-1 align-items-center justify-content-center">
                                                <span>{formData.siret || <em className="text-muted">(vide)</em>}</span>
                                                <EditButton onClick={() => startEditing('siret', formData.siret)} />
                                                <Button variant="danger" size="sm" className="p-2 m-1" onClick={() => deselect('siret', () => setField('siret', null))}><i className="fas fa-redo" /></Button>
                                            </div>
                                            {isSiretInvalid &&
                                                <div className="text-danger small mt-1">SIRET invalide : 14 chiffres attendus avec une clé de contrôle correcte.</div>
                                            }
                                            {siretOrganizationInfo()}
                                        </div>
                                }
                            </td>
                        </tr>
                        <tr>
                            <td>UAI</td>
                            <td
                                {...(boClient && { onClick: () => { setField('uai', pipedriveClient?.uai ?? ''); selectSource('uai', 'pipedrive'); }, style: { cursor: 'pointer', ...(selectedSources.uai === 'pipedrive' && { fontWeight: 'bold' }) }, title: 'Cliquer pour sélectionner' })}
                            >
                                {pipedriveData.uai}
                            </td>
                            {boClient && <td
                                onClick={() => { setField('uai', boClient.uai ?? ''); selectSource('uai', 'bo'); }}
                                style={{ cursor: 'pointer', ...(selectedSources.uai === 'bo' && { fontWeight: 'bold' }) }}
                                title="Cliquer pour sélectionner"
                            >
                                {boData.uai}
                            </td>}
                            <td className="text-center">
                                {formData.uai === null
                                    ? (boClient && (pipedriveClient?.uai || boClient.uai))
                                        ? <span className="text-muted fst-italic small">Sélectionnez une source ci-contre</span>
                                        : <EditButton onClick={() => { startEditing('uai', null); setField('uai', pipedriveClient?.uai ?? boClient?.uai ?? ''); }} />
                                    : editingFields.uai
                                        ? <div>
                                            <div className="d-flex gap-1 align-items-center justify-content-center">
                                                <Form.Control size="sm" value={formData.uai} isInvalid={isUaiInvalid} onChange={e => setField('uai', e.target.value)} />
                                                <Button variant="success" size="sm" className="p-2 m-1" disabled={isUaiInvalid} onClick={() => stopEditing('uai')}><i className="fas fa-check" /></Button>
                                                <Button variant="outline-secondary" size="sm" className="p-2 m-1" onClick={() => cancelEditing('uai')}><i className="fas fa-times" /></Button>
                                            </div>
                                            {isUaiInvalid &&
                                                <div className="text-danger small mt-1">{UAI_INVALID_MESSAGE}</div>
                                            }
                                        </div>
                                        : <div>
                                            <div className="d-flex gap-1 align-items-center justify-content-center">
                                                <span>{formData.uai || <em className="text-muted">(vide)</em>}</span>
                                                <EditButton onClick={() => startEditing('uai', formData.uai)} />
                                                <Button variant="danger" size="sm" className="p-2 m-1" onClick={() => deselect('uai', () => setField('uai', null))}><i className="fas fa-redo" /></Button>
                                            </div>
                                            {isUaiInvalid &&
                                                <div className="text-danger small mt-1">{UAI_INVALID_MESSAGE}</div>
                                            }
                                            {uaiEstablishmentInfo()}
                                        </div>
                                }
                            </td>
                        </tr>
                        <tr>
                            <td>Client Parent</td>
                            <td
                                {...(boClient && pipedriveData.parentClientId && { onClick: () => { setParent([pipedriveData.parentClientId]); selectSource('parentClient', 'pipedrive'); setParentEditing(false); }, style: { cursor: 'pointer', ...(selectedSources.parentClient === 'pipedrive' && { fontWeight: 'bold' }) }, title: 'Cliquer pour sélectionner' })}
                            >
                                {isFetchingAllClients ? <Loader /> : pipedriveData.parentClient}
                            </td>
                            {boClient && <td
                                onClick={() => { setParent(boData.parentClientId ? [boData.parentClientId] : []); selectSource('parentClient', 'bo'); setParentEditing(false); }}
                                style={{ cursor: 'pointer', ...(selectedSources.parentClient === 'bo' && { fontWeight: 'bold' }) }}
                                title={boData.hasParent ? 'Cliquer pour sélectionner' : 'Cliquer pour sélectionner (pas de parent)'}
                            >
                                {isFetchingAllClients ? <Loader /> : boData.parentClient}
                            </td>}
                            <td className="text-center">
                                {pipedriveData.parentRelationType === 'related' &&
                                    <Alert variant="warning" className="mb-1 text-start">Dans PipeDrive, ce client est lié à cette organisation par une relation "liée" (related), pas "parent". Vous pouvez tout de même le désigner comme parent dans le BO — la relation devra être corrigée manuellement dans PipeDrive.</Alert>
                                }
                                {pipedriveData.parentNeedsBOAccess &&
                                    <Alert variant="danger" className="mb-1 text-start">Le client parent identifié dans PipeDrive n'a pas encore d'accès BO — il doit être créé/qualifié dans le BO avant de pouvoir être désigné comme parent.</Alert>
                                }
                                {!isFetchingAllClients
                                    ? formData.parentClientUuid === null
                                        ? parentEditing
                                            ? <>
                                                <Button variant="outline-secondary" size="sm" className="p-2 m-1" onClick={() => setParentEditing(false)}><i className="fas fa-times" /></Button>
                                                <SelectSingleClientComponent
                                                    nameSelect={"clientSelector"}
                                                    selectedClients={[]}
                                                    setSelectedClients={(uuid) => { setParent(uuid); setParentEditing(false); }}
                                                    initialClients={allClients}
                                                    mustRedirect={false}
                                                />
                                            </>
                                            : <div className="d-flex gap-1 flex-wrap align-items-center justify-content-center">
                                                {boClient && <span className="text-muted fst-italic small me-1">Sélectionnez une source ci-contre ou :</span>}
                                                <Button size="sm" variant="warning" onClick={() => { setParent([]); selectSource('parentClient', 'manual'); }}>Pas de parent</Button>
                                                <EditButton onClick={() => setParentEditing(true)} />
                                            </div>
                                        : parentEditing
                                            ? <>
                                                <Button variant="outline-secondary" size="sm" className="p-2 m-1" onClick={() => setParentEditing(false)}><i className="fas fa-times" /></Button>
                                                <SelectSingleClientComponent
                                                    nameSelect={"clientSelectorEdit"}
                                                    selectedClients={formData.parentClientUuid}
                                                    setSelectedClients={(uuid) => { setParent(uuid); setParentEditing(false); }}
                                                    initialClients={allClients}
                                                    mustRedirect={false}
                                                />
                                            </>
                                            : <div className="d-flex gap-1 align-items-center justify-content-center">
                                                {formData.parentClientUuid.length === 0
                                                    ? <span className="fst-italic text-muted">Pas de parent</span>
                                                    : allClients?.find(c => c.uniqueId === formData.parentClientUuid[0])?.clientName ?? formData.parentClientUuid[0]
                                                }
                                                <EditButton onClick={() => setParentEditing(true)} />
                                                <Button variant="danger" size="sm" className="p-2 m-1" onClick={() => deselect('parentClient', () => { setParent(null); setParentEditing(false); })}><i className="fas fa-redo" /></Button>
                                            </div>
                                    : <Loader />
                                }
                            </td>
                        </tr>
                        <tr>
                            <td>Adresse</td>
                            <td
                                {...(boClient && { onClick: () => { initAddress(pipedriveData); selectSource('address', 'pipedrive'); }, style: { cursor: 'pointer', ...(selectedSources.address === 'pipedrive' && { fontWeight: 'bold' }) }, title: 'Cliquer pour sélectionner' })}
                            >
                                {pipedriveData.addressLocality ? <>
                                    <div><span className="fw-bold text-muted me-1">Rue :</span>{pipedriveData.addressStreet}</div>
                                    <div><span className="fw-bold text-muted me-1">Ville :</span>{pipedriveData.addressLocality}</div>
                                    <div><span className="fw-bold text-muted me-1">Code postal :</span>{pipedriveData.addressPostalCode || '-'}</div>
                                    <div><span className="fw-bold text-muted me-1">Pays :</span>{pipedriveData.addressCountry || '-'}</div>
                                </> : pipedriveData.address}
                            </td>
                            {boClient && <td
                                onClick={() => { initAddress(boData); selectSource('address', 'bo'); }}
                                style={{ cursor: 'pointer', ...(selectedSources.address === 'bo' && { fontWeight: 'bold' }) }}
                                title="Cliquer pour sélectionner"
                            >
                                {boData.addressLocality ? <>
                                    <div><span className="fw-bold text-muted me-1">Rue :</span>{boData.addressStreet}</div>
                                    <div><span className="fw-bold text-muted me-1">Ville :</span>{boData.addressLocality}</div>
                                    <div><span className="fw-bold text-muted me-1">Code postal :</span>{boData.addressPostalCode || '-'}</div>
                                    <div><span className="fw-bold text-muted me-1">Pays :</span>{boData.addressCountry || '-'}</div>
                                </> : boData.address}
                            </td>}
                            <td className="text-center">
                                {formData.addressLocality === null
                                    ? (boClient && (pipedriveData.addressLocality || boData?.addressLocality))
                                        ? <span className="text-muted fst-italic small">Sélectionnez une source ci-contre</span>
                                        : <EditButton onClick={() => { startEditing('address', addressSnapshot()); initAddress(pipedriveData); }} />
                                    : editingFields.address
                                        ? <>
                                            <div className="d-flex gap-1 justify-content-center mb-1">
                                                <Button variant="success" size="sm" className="p-2 m-1" onClick={() => stopEditing('address')}><i className="fas fa-check" /></Button>
                                                <Button variant="outline-secondary" size="sm" className="p-2 m-1" onClick={() => cancelEditing('address', restoreAddress)}><i className="fas fa-times" /></Button>
                                            </div>
                                            <div className="d-flex gap-1 mb-1 justify-content-center">
                                                <Form.Control size="sm" placeholder="N°" style={{ width: '4rem' }} value={formData.addressStreetNumber} onChange={e => setField('addressStreetNumber', e.target.value)} />
                                                <Form.Control size="sm" placeholder="Rue" value={formData.addressRoute} onChange={e => setField('addressRoute', e.target.value)} />
                                            </div>
                                            <div className="d-flex gap-1 mb-1 justify-content-center">
                                                <Form.Control size="sm" placeholder="Code postal" style={{ width: '7rem' }} value={formData.addressPostalCode} onChange={e => setField('addressPostalCode', e.target.value)} />
                                                <Form.Control size="sm" placeholder="Ville" value={formData.addressLocality} onChange={e => setField('addressLocality', e.target.value)} />
                                            </div>
                                            <Form.Control size="sm" placeholder="Pays" value={formData.addressCountry} onChange={e => setField('addressCountry', e.target.value)} />
                                        </>
                                        : <>
                                            <div className="mb-1">
                                                {[formData.addressStreetNumber, formData.addressRoute, formData.addressPostalCode, formData.addressLocality, formData.addressCountry].some(Boolean)
                                                    ? <>
                                                        <div><span className="fw-bold text-muted me-1">Rue :</span>{[formData.addressStreetNumber, formData.addressRoute].filter(Boolean).join(' ') || '-'}</div>
                                                        <div><span className="fw-bold text-muted me-1">Ville :</span>{formData.addressLocality || '-'}</div>
                                                        <div><span className="fw-bold text-muted me-1">Code postal :</span>{formData.addressPostalCode || '-'}</div>
                                                        <div><span className="fw-bold text-muted me-1">Pays :</span>{formData.addressCountry || '-'}</div>
                                                    </>
                                                    : <em className="text-muted">(vide)</em>
                                                }
                                            </div>
                                            <div className="d-flex gap-1 justify-content-center">
                                                <EditButton onClick={() => startEditing('address', addressSnapshot())} />
                                                <Button variant="danger" size="sm" className="p-2 m-1" onClick={() => deselect('address', cancelAddress)}><i className="fas fa-redo" /></Button>
                                            </div>
                                        </>
                                }
                            </td>
                        </tr>
                    </tbody>
                </Table>
            </Row>
            {contacts.length > 0 && <Row>
                <h3 className="mb-4 mt-4">Contacts</h3>
                <Table striped bordered>
                    <thead>
                        <tr>
                            <th>Pipedrive</th>
                            {boClient && <th>Backoffice</th>}
                            <th>Informations gardées</th>
                        </tr>
                    </thead>
                    <tbody>
                        {contacts.map((contact) => {
                            const editedContact = formData.contacts.find(c => c.id === contact.id);
                            const boContact = message.clientBoData?.contacts?.find(c => contact.id == c.idPipedrive || c.email === contact.email);
                            const contactKey = `contact_${contact.id}`;
                            const isEditingContact = !!editingFields[contactKey];
                            const cellClickable = boContact && !isEditingContact;
                            return (
                                <tr key={contact.id}>
                                    <td
                                        onClick={cellClickable ? () => { initContactFrom(contact.id, contact, boContact.uniqueId); selectSource(contactKey, 'pipedrive'); } : undefined}
                                        style={{ ...(cellClickable && { cursor: 'pointer' }), ...(selectedSources[contactKey] === 'pipedrive' && { fontWeight: 'bold' }) }}
                                        title={cellClickable ? 'Cliquer pour sélectionner' : undefined}
                                    >
                                        <ContactFields contact={contact} />
                                    </td>
                                    {boClient && <td
                                        onClick={cellClickable ? () => { initContactFrom(contact.id, boContact, boContact.uniqueId); selectSource(contactKey, 'bo'); } : undefined}
                                        style={{ ...(cellClickable && { cursor: 'pointer' }), ...(selectedSources[contactKey] === 'bo' && { fontWeight: 'bold' }) }}
                                        title={cellClickable ? 'Cliquer pour sélectionner' : undefined}
                                    >
                                        {boContact
                                            ? <ContactFields contact={boContact} />
                                            : <span className="text-muted fst-italic">Non trouvé dans le BO</span>}
                                    </td>}
                                    <td className="text-center">
                                        {!editedContact && !boContact && (
                                            <div className="d-flex gap-2 justify-content-center mb-2">
                                                <Button
                                                    variant="success"
                                                    size="sm"
                                                    className="p-2 m-1"
                                                    onClick={() => { initContactFrom(contact.id, contact); selectSource(contactKey, 'pipedrive'); }}
                                                >
                                                    Ajouter au BO
                                                </Button>
                                            </div>
                                        )}
                                        {!editedContact &&
                                            <span className="text-muted fst-italic small">Non modifié</span>
                                        }
                                        {editedContact && (
                                            isEditingContact
                                                ? <>
                                                    <div className="d-flex gap-1 justify-content-center mb-1">
                                                        <Button size="sm" variant="success" className="p-2 m-1" onClick={() => stopEditing(contactKey)}><i className="fas fa-check" /></Button>
                                                        <Button size="sm" variant="outline-secondary" className="p-2 m-1" onClick={() => cancelEditing(contactKey, () => restoreContact(contact.id))}><i className="fas fa-times" /></Button>
                                                    </div>
                                                    <div className="mb-1"><Form.Control size="sm" placeholder="Nom" value={editedContact.name} onChange={e => setContactField(contact.id, 'name', e.target.value)} /></div>
                                                    <div className="mb-1"><Form.Control size="sm" placeholder="Fonction" value={editedContact.jobTitle} onChange={e => setContactField(contact.id, 'jobTitle', e.target.value)} /></div>
                                                    <div className="mb-1">
                                                        <Form.Control size="sm" id={`contact-email-${contact.id}`} placeholder="Email" value={editedContact.email} isInvalid={isEmailInvalid(editedContact.email)} onChange={e => setContactField(contact.id, 'email', e.target.value)} />
                                                        <Form.Control.Feedback type="invalid">Email invalide</Form.Control.Feedback>
                                                    </div>
                                                    <div><Form.Control size="sm" placeholder="Téléphone" value={editedContact.phone} onChange={e => setContactField(contact.id, 'phone', e.target.value)} /></div>
                                                </>
                                                : <>
                                                    <div className="mb-1">
                                                        <div><span className="fw-bold text-muted me-1">Nom :</span>{editedContact.name || <em className="text-muted">(vide)</em>}</div>
                                                        <div><span className="fw-bold text-muted me-1">Fonction :</span>{editedContact.jobTitle || <em className="text-muted">(vide)</em>}</div>
                                                        <div className={isEmailInvalid(editedContact.email) ? 'text-danger' : undefined}>
                                                            <span className="fw-bold text-muted me-1">Email :</span>{editedContact.email || <em className="text-muted">(vide)</em>}
                                                            {isEmailInvalid(editedContact.email) && <><i className="fas fa-exclamation-triangle ms-1" /> <span className="small">(invalide)</span></>}
                                                        </div>
                                                        <div><span className="fw-bold text-muted me-1">Téléphone :</span>{editedContact.phone || <em className="text-muted">(vide)</em>}</div>
                                                    </div>
                                                    <div className="d-flex gap-1 justify-content-center">
                                                        <EditButton onClick={() => startEditing(contactKey, editedContact)} />
                                                        <Button size="sm" variant="danger" className="p-2 m-1" onClick={() => deselect(contactKey, () => cancelContact(contact.id))}><i className="fas fa-redo" /></Button>
                                                    </div>
                                                </>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </Table>
            </Row>}

            <h3 className="mb-4 mt-4">Qualification Client</h3>
            <Row className="mb-4">
                {clientsCategories?.length > 0 &&
                    <Col xs={allTagOperations?.length > 0 ? 6 : 12}>
                        <SelectMultipleItemsWithSearchBar idSelector="clientCRM-Category-Select" label="Catégories"
                            allItems={clientsCategories}
                            selectedItemKeys={formData.categories ?? derivedCategories} setSelectedItemKeys={(ids) => setField('categories', ids)}
                            itemKey="id" itemValue="name" />
                        <Form.Control.Feedback type='invalid' id="invalid-feedback-categories">Veuillez donner une catégorie au client</Form.Control.Feedback>
                    </Col>
                }
                {allTagOperations?.length > 0 &&
                    <Col xs={clientsCategories?.length > 0 ? 6 : 12}>
                        <SelectMultipleItemsWithSearchBar idSelector="clientCRM-Tags-Select" label="Opé M360"
                            allItems={allTagOperations}
                            selectedItemKeys={formData.tagOperations ?? derivedTagOperations} setSelectedItemKeys={(ids) => setField('tagOperations', ids)}
                            itemKey="id" itemValue="name" />
                    </Col>
                }
            </Row>
            <Row className="mb-4">
                {allClientStatus && <Col>
                    <p className='form-title'>Statut</p>
                    {!isFetchingAllClientStatus &&
                        <Form.Control
                            as="select" size="sm" aria-label="Choix statut"
                            name='clientStatus'
                            value={formData.clientStatus ?? boClient?.statut ?? ''}
                            onChange={e => setField('clientStatus', e.target.value)}
                        >
                            <option value="" disabled>Choisissez un statut</option>
                            {allClientStatus?.map((status) => (
                                <option
                                    key={'row_status' + '_' + status.id}
                                    value={status.id}
                                >
                                    {status.name}
                                </option>
                            ))}
                        </Form.Control>
                    }
                    <Form.Control.Feedback type='invalid'>Veuillez donner un statut au client</Form.Control.Feedback>
                </Col>}
                {allRelationTypes?.length > 0 &&
                    <Col xs={allRelationTypes?.length > 0 ? 6 : 12}>
                        <SelectMultipleItemsWithSearchBar idSelector="clientCRM-relationTypes-Select" label="Nature de la relation"
                            allItems={allRelationTypes}
                            selectedItemKeys={formData.natureRelation ?? derivedNatureRelation} setSelectedItemKeys={(ids) => setField('natureRelation', ids)}
                            itemKey="id" itemValue="name" />
                    </Col>
                }
            </Row>
            {contactsM360 && <>
                <Row>
                    <Col>
                        <SelectMultipleItemsWithSearchBar idSelector="clientCRM-ContactsM360-Select" label="Chargé⋅es de clientèle"
                            allItems={contactsM360}
                            selectedItemKeys={formData.contactsM360 ?? boClient?.contactClientM360 ?? []} setSelectedItemKeys={(ids) => setField('contactsM360', ids)}
                            itemKey="id" itemValue="name" />
                    </Col>
                </Row>
            </>}
            {message.pipedriveProducts?.length > 0
                ? <>
                <hr />
                <Row>
                    <Col>
                        <h3 className="mb-4 mt-4">Devis</h3>
                        <Table bordered size="sm">
                            <thead>
                                <tr>
                                    <th>Nom</th>
                                    <th>Qté</th>
                                    <th>Prix unitaire</th>
                                    <th>TVA</th>
                                    <th>Remise</th>
                                    <th>Total</th>
                                    <th>Durée</th>
                                        <th>Erreur</th>
                                </tr>
                            </thead>
                            <tbody>
                                {message.pipedriveProducts.map((p, i) => (
                                    <tr key={i}>
                                        <td>{p.name}{p.idAxonaut && <span className="text-muted small ms-1">(#{p.idAxonaut})</span>}</td>
                                        <td>{p.quantity}</td>
                                        <td>{p.unitPrice} {p.currency}</td>
                                        <td>{p.vatRate != null ? `${p.vatRate}%` : '-'}</td>
                                        <td>{p.discount != null ? (p.discountType === 'amount' ? `${p.discount} ${p.currency}` : `${p.discount}%`) : '-'}</td>
                                        <td>{p.sum} {p.currency}</td>
                                        <td>{p.duration != null ? `${p.duration} ${p.durationUnit ?? ''}`.trim() : '-'}</td>
                                        <td>{p.error ? <span className="text-danger">{p.error}</span> : "-"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </Col>
                </Row>
                </>
                : (message.type == "deal" &&
                    <Alert variant='danger'>Ce devis ne contient aucun produit.</Alert>
                )
            }
            {message.type == "deal" &&
                <Row className="mb-3">
                    <Col>
                        <strong>Client Facturé</strong>
                        <Form.Select value={formData?.clientInvoiced ?? 'self'} onChange={(e) => handleInvoiceSelect(e)}>
                            {invoiceOptions}
                        </Form.Select>
                    </Col>
                    {formData?.clientInvoiced === 'self' && <Col>
                        <strong>Contact de facturation</strong>
                        <Form.Select
                            value={formData?.billingContactId ?? ''}
                            isInvalid={isBillingEmailInvalid}
                            onChange={e => setField('billingContactId', e.target.value || null)}
                        >
                            <option value="">Aucun</option>
                            {contacts.map(pipContact => formData.contacts.find(c => String(c.id) === String(pipContact.id)) ?? pipContact).map(c => (
                                <option key={c.id} value={c.id}>
                                    {c.name || c.email || `Contact #${c.id}`}{c.email && c.name ? ` — ${c.email}` : ''}
                                </option>
                            ))}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">L'email de ce contact est invalide.</Form.Control.Feedback>
                    </Col>}
                </Row>}
            <hr />
            <Row>
                <Col>
                    <Form.Label>Note</Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={3}
                        value={note}
                        onChange={e => setNote(e.target.value)}
                        placeholder="Ajouter une note..."
                    />
                </Col>
            </Row>
            {(invalidEmailContacts.length > 0 || (isBillingEmailInvalid && !isBillingContactKept)) &&
                <Alert variant="danger" className="mt-3">
                    {invalidEmailContacts.length > 0 && <>
                        Les contacts suivants ont un email invalide, corrigez-les avant de valider :
                        <ul className="mb-0">
                            {invalidEmailContacts.map(c => (
                                <li key={c.id}>{c.name || `Contact #${c.id}`} : <strong>{c.email}</strong></li>
                            ))}
                        </ul>
                    </>}
                    {isBillingEmailInvalid && !isBillingContactKept &&
                        <div className={invalidEmailContacts.length > 0 ? 'mt-2' : undefined}>
                            Le contact de facturation <strong>{billingContact.name || `Contact #${billingContact.id}`}</strong> a un email invalide (<strong>{billingContact.email}</strong>).
                            Corrigez son email ou choisissez un autre contact de facturation.
                            <Button variant="success" size="sm" className="ms-2" onClick={fixBillingContactEmail}>
                                Corriger l'email
                            </Button>
                        </div>
                    }
                </Alert>
            }
            <Row className="mt-3">
                <Col className="d-flex justify-content-between align-items-center">
                    <div>
                        {AuthorizationChecker.hasDeleteRights('pipedriveMessage') &&
                            <Button variant="danger" disabled={isSubmitting} onClick={createDeleteModal}>
                                <i className="fas fa-trash-alt" />&nbsp;Supprimer
                            </Button>
                        }
                    </div>
                    <div className="d-flex gap-2">
                        <Button variant="secondary" disabled={isSubmitting} onClick={() => submitEdit(4)}>Ignorer</Button>
                        <Button variant="warning" disabled={isSubmitting || isSiretInvalid || isUaiInvalid} onClick={() => submitEdit(2)}>Mise en attente</Button>
                        <Button variant="success" disabled={isSubmitting || isSiretInvalid || isUaiInvalid || invalidEmailContacts.length > 0 || isBillingEmailInvalid} onClick={() => submitEdit(2, true)}>Valider</Button>
                    </div>
                </Col>
            </Row>
            {modalComponent}
        </Container>
    );
};

export default PipedriveDetailPage;
